import type {
    BioscopeVideoStreamSettings,
    ClientVideoStreamSettings,
    LocationVideoStreamSettingsGroup,
    StreamQuality,
    VideoStreamSettingsValues,
} from './types';

interface ApiVideoStreamSettingsValues {
    dailyLimitSeconds?: number;
    maxContinuousDurationSeconds?: number;
    defaultQuality?: string;
}

interface ApiBioscopeStreamSettingsRow {
    bioscope?: {
        id?: number;
        version?: string;
        lastAlive?: string | null;
    };
    location?: {
        id?: number;
        name?: string;
        timezone?: string;
    };
    cage?: {
        id?: number;
        name?: string;
    };
    settings?: ApiVideoStreamSettingsValues;
    currentDailyUsageSeconds?: number;
}

export type MaybeSnakeStreamSettings = ApiVideoStreamSettingsValues & {
    daily_limit_seconds?: number;
    max_continuous_duration_seconds?: number;
    default_quality?: string;
};

/**
 * API responses may use camelCase (DTO) or, if ever proxied, snake_case — accept both
 * so we do not fall back to defaults and show stale 3600/300/HD in the table.
 */
export function mapVideoStreamSettingsValuesToDomain(
    input?: MaybeSnakeStreamSettings
): VideoStreamSettingsValues {
    const d =
        input?.dailyLimitSeconds !== undefined
            ? input.dailyLimitSeconds
            : input?.daily_limit_seconds;
    const m =
        input?.maxContinuousDurationSeconds !== undefined
            ? input.maxContinuousDurationSeconds
            : input?.max_continuous_duration_seconds;
    const q = input?.defaultQuality ?? input?.default_quality;
    const quality: StreamQuality = q === 'STANDARD' ? 'STANDARD' : 'HD';

    return {
        dailyLimitSeconds: d !== undefined && d !== null ? Number(d) : 3600,
        maxContinuousDurationSeconds: m !== undefined && m !== null ? Number(m) : 300,
        defaultQuality: quality,
    };
}

export function areVideoStreamSettingsEqual(
    a: VideoStreamSettingsValues,
    b: VideoStreamSettingsValues
): boolean {
    return (
        a.dailyLimitSeconds === b.dailyLimitSeconds &&
        a.maxContinuousDurationSeconds === b.maxContinuousDurationSeconds &&
        a.defaultQuality === b.defaultQuality
    );
}

export function mapBioscopeStreamSettingsRowsToDomain(
    rows: ApiBioscopeStreamSettingsRow[]
): BioscopeVideoStreamSettings[] {
    return rows
        .map((row): BioscopeVideoStreamSettings | null => {
            const bioscopeId = row.bioscope?.id;
            const locationId = row.location?.id;
            const cageId = row.cage?.id;
            if (!bioscopeId || !locationId || !cageId) {
                return null;
            }

            return {
                bioscope: {
                    id: bioscopeId,
                    version: row.bioscope?.version || 'unknown',
                    lastAlive: row.bioscope?.lastAlive ?? null,
                },
                location: {
                    id: locationId,
                    name: row.location?.name || 'Unknown location',
                    timezone: row.location?.timezone || 'UTC',
                },
                cage: {
                    id: cageId,
                    name: row.cage?.name || 'Unknown cage',
                },
                settings: mapVideoStreamSettingsValuesToDomain(row.settings),
                currentDailyUsageSeconds: row.currentDailyUsageSeconds ?? 0,
            };
        })
        .filter((row): row is BioscopeVideoStreamSettings => row !== null);
}

export function groupBioscopeSettingsByLocation(
    rows: BioscopeVideoStreamSettings[]
): LocationVideoStreamSettingsGroup[] {
    const grouped = rows.reduce<Record<number, LocationVideoStreamSettingsGroup>>((acc, row) => {
        const existing = acc[row.location.id];
        if (!existing) {
            acc[row.location.id] = {
                locationId: row.location.id,
                locationName: row.location.name,
                timezone: row.location.timezone,
                bioscopes: [row],
            };
            return acc;
        }

        existing.bioscopes.push(row);
        return acc;
    }, {});

    return Object.values(grouped)
        .map((group) => ({
            ...group,
            bioscopes: [...group.bioscopes].sort((a, b) => a.cage.name.localeCompare(b.cage.name)),
        }))
        .sort((a, b) => a.locationName.localeCompare(b.locationName));
}

export function mapClientVideoStreamSettings(
    rows: ApiBioscopeStreamSettingsRow[]
): ClientVideoStreamSettings {
    const mappedRows = mapBioscopeStreamSettingsRowsToDomain(rows);
    return {
        rows: mappedRows,
        locations: groupBioscopeSettingsByLocation(mappedRows),
    };
}
